import {
  AbstractAssetTask,
  AbstractMesh,
  AnimationGroup,
  ContainerAssetTask,
  Mesh,
  MeshAssetTask,
  Scene,
  Skeleton,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { GameConfig } from './GameConfig';

export enum AssetType {
  MESH,
  CONTAINER,
  IMAGE,
  TEXT,
  BINARY,
}
export class Asset {
  task: AbstractAssetTask;
  usedMeshes: string[] = [];

  static findByName(assets: Asset[], name: string): Asset {
    return assets.find((asset) => {
      return asset.name === name;
    });
  }

  constructor(
    public name: string,
    public type: AssetType,
    public rootUrl: string,
    public fileName: string,
    public meshNames: any = '',
    public meshName: string = undefined,
    public resetPosition: boolean = true,
    public unParent: boolean = true,
    public singleMesh: boolean = false,
    public meshIndex: number = undefined
  ) {}

  withMeshNames(meshNames: string): Asset {
    this.meshNames = meshNames;
    return this;
  }

  withMeshName(meshName: string): Asset {
    this.singleMesh = true;
    this.meshName = meshName;
    return this;
  }

  withResetPosition(resetPosition: boolean): Asset {
    this.resetPosition = resetPosition;
    return this;
  }

  withUnParent(unParent: boolean): Asset {
    this.unParent = unParent;
    return this;
  }

  withMeshIndex(meshIndex: number): Asset {
    this.meshIndex = meshIndex;
    return this;
  }

  withSingleMesh(singleMesh: boolean): Asset {
    this.singleMesh = singleMesh;
    return this;
  }

  getTask<T>(type?: T): T {
    return this.task as T;
  }

  removeMeshes(scene: Scene, excludes: string[] = []) {
    const isExcluded = (name: string) => {
      for (const exclude of excludes) {
        if (name.includes(exclude)) {
          return true;
        }
        if (name.includes(exclude.replace('*', ''))) {
          return true;
        }
      }
      return false;
    };
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      if (GameConfig.logDebug) {
        console.debug(
          'Removing transform nodes',
          this.task.loadedTransformNodes.map((n) => n.name),
          'from asset',
          this.name
        );
      }
      for (const tn of this.task.loadedTransformNodes) {
        if (isExcluded(tn.name)) {
          if (GameConfig.logDebug) {
            console.debug('Did not Remove', tn.name, 'from asset', this.name, 'because it was excluded');
          }
          continue;
        }
        scene.removeTransformNode(tn);
        tn.dispose(true);
      }
      if (GameConfig.logDebug) {
        console.debug(
          'Removing meshes',
          this.task.loadedMeshes.map((n) => n.name),
          'from asset',
          this.name
        );
      }
      for (const mesh of this.task.loadedMeshes) {
        if (isExcluded(mesh.name)) {
          if (GameConfig.logDebug) {
            console.debug('Did not Remove', mesh.name, 'from asset', this.name, 'because it was excluded');
          }
          continue;
        }
        if (mesh.physicsBody) {
          if (GameConfig.logDebug) {
            console.debug('Removing physics body for', mesh.name, 'from asset', this.name);
          }
          mesh.physicsBody.dispose();
        }
        scene.removeMesh(mesh, true);
        mesh.dispose(false, true);
      }
    }
  }

  getMeshNames(): string[] {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      return this.task.loadedMeshes.map((m) => {
        return m.name;
      });
    }
    return [];
  }

  getMeshIds(): string[] {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      return this.task.loadedMeshes.map((m) => {
        return m.id;
      });
    }
    return [];
  }

  static filterMeshes(list: AbstractMesh[]): Mesh[] {
    if (!list || !Array.isArray(list)) {
      return [];
    }
    // only meshes, no transform nodes, no instanced meshes, no empty meshes
    return list.filter((m): m is Mesh => m instanceof Mesh && !!m && !!m.geometry && m.getTotalVertices() > 0);
  }

  getSingleMesh(): AbstractMesh {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      const meshes = Asset.filterMeshes(this.task.loadedMeshes);
      if (this.meshName) {
        return meshes.find((m) => m.name === this.meshName);
      }
      if (meshes.length === 0) {
        return undefined;
      }
      if (meshes.length === 1) {
        return meshes[0];
      }

      const merged = Mesh.MergeMeshes(meshes, true, true, undefined, true, true);
      return merged ?? undefined;
    }
    return undefined;
  }

  getTransformNode(name?: string): TransformNode {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      const tn = name
        ? this.task.loadedTransformNodes.find((tn) => {
            return tn.name === name;
          })
        : this.task.loadedTransformNodes[0];

      if (tn) {
        if (this.resetPosition) {
          tn.setAbsolutePosition(Vector3.Zero());
        }
      }

      return tn;
    }
    return undefined;
  }

  getMesh(name: string): Mesh {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      const tn = this.task.loadedMeshes.find((tn) => {
        return tn.name === name;
      });
      return tn as Mesh;
    }
    return undefined;
  }

  getMeshByIndex(index: number): Mesh {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      const mesh = this.task.loadedMeshes[index];
      return mesh as Mesh;
    }
    return undefined;
  }

  useMesh(name: string) {
    this.usedMeshes.push(name);
  }

  removeUnusedMeshes(scene: Scene) {
    for (const meshName of this.getMeshNames()) {
      if (this.usedMeshes.includes(meshName)) {
        continue;
      }
      const mesh = scene.getMeshByName(meshName);
      if (mesh) {
        if (GameConfig.logDebug) {
          console.log('Removed mesh', meshName, 'because it was not used');
        }
        if (mesh.physicsBody) {
          mesh.physicsBody.dispose();
        }
        mesh.dispose();
      }
    }
  }

  getMeshes(name?: string | string[]): AbstractMesh[] {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      let meshes: AbstractMesh[] = [];
      if (this.unParent) {
        (this.task.loadedMeshes[0] as Mesh).setEnabled(false);
        for (let i = 1; i < this.task.loadedMeshes.length; i++) {
          this.task.loadedMeshes[i].setParent(null);
          const mesh = this.task.loadedMeshes[i] as Mesh;
          //mesh.setEnabled(false);
          if (this.resetPosition) {
            mesh.setAbsolutePosition(Vector3.Zero());
          }
          meshes.push(mesh);
        }
      } else {
        for (let i = 0; i < this.task.loadedMeshes.length; i++) {
          const mesh = this.task.loadedMeshes[i] as Mesh;
          if (this.resetPosition) {
            mesh.setAbsolutePosition(Vector3.Zero());
          }
          meshes.push(mesh);
        }
      }

      if (name) {
        meshes = meshes.filter((mesh: AbstractMesh, index: number, array: AbstractMesh[]) => {
          if (Array.isArray(name)) {
            return name.includes(mesh.name);
          } else {
            if (name.includes('*')) {
              return mesh.name.includes(name.replace('*', ''));
            }
            return mesh.name === name;
          }
        });
      }
      return meshes;
    }
    return [];
  }

  /**
   * Get the skeleton from the loaded asset
   */
  getSkeleton(): Skeleton | null {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      const skeletons = this.task.loadedSkeletons;
      return skeletons && skeletons.length > 0 ? skeletons[0] : null;
    }
    return null;
  }

  /**
   * Get animation groups from the loaded asset
   */
  getAnimationGroups(): AnimationGroup[] {
    if (this.task instanceof MeshAssetTask || this.task instanceof ContainerAssetTask) {
      return this.task.loadedAnimationGroups || [];
    }
    return [];
  }
}
